import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MatDateFormats,
} from '@angular/material/core';
import { MatCalendar } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { EventCalendarService } from '../../services/event-calendar.service';

@Component({
  selector: 'app-event-calendar-header',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule],
  styles: [
    `
      .event-calendar-header {
        display: flex;
        align-items: center;
        padding: 0.5em;
      }

      .event-calendar-header-label {
        flex: 1;
        height: 1em;
        font-weight: 500;
        text-align: left;
      }

      .event-calendar-double-arrow .mat-icon {
        margin: -22%;
      }
    `,
  ],
  template: `
    <div class="event-calendar-header">
      <span class="event-calendar-header-label">{{ periodLabel }}</span>
      <button mat-icon-button (click)="previousClicked('month')">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          fill="currentColor"
          class="bi bi-chevron-left"
          viewBox="0 0 16 16">
          <path
            fill-rule="evenodd"
            d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z" />
        </svg>
      </button>
      <button mat-icon-button (click)="nextClicked('month')">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          fill="currentColor"
          class="bi bi-chevron-right"
          viewBox="0 0 16 16">
          <path
            fill-rule="evenodd"
            d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z" />
        </svg>
      </button>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventCalendarHeaderComponent<D> implements OnInit, OnDestroy {
  private destroyed = new Subject<void>();

  constructor(
    private eventCalendarService: EventCalendarService,
    private calendar: MatCalendar<D>,
    private dateAdapter: DateAdapter<D>,
    @Inject(MAT_DATE_FORMATS) private dateFormats: MatDateFormats,
    cdr: ChangeDetectorRef,
  ) {
    calendar.stateChanges
      .pipe(takeUntil(this.destroyed))
      .subscribe(() => cdr.markForCheck());

    this.eventCalendarService.setActiveMonth
      .pipe(takeUntil(this.destroyed))
      .subscribe((d) => (this.calendar.activeDate = d as any));
  }

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  get periodLabel(): string {
    return this.dateAdapter
      .format(this.calendar.activeDate, this.dateFormats.display.monthYearLabel)
      .toLocaleUpperCase();
  }

  previousClicked(mode: 'month' | 'year'): void {
    if (mode === 'month') {
      this.calendar.activeDate = this.dateAdapter.addCalendarMonths(
        this.calendar.activeDate,
        -1,
      );
    } else {
      this.calendar.activeDate = this.dateAdapter.addCalendarYears(
        this.calendar.activeDate,
        -1,
      );
    }

    this.eventCalendarService.changedActiveMonth.next(this.calendar.activeDate);
  }

  nextClicked(mode: 'month' | 'year'): void {
    if (mode === 'month') {
      this.calendar.activeDate = this.dateAdapter.addCalendarMonths(
        this.calendar.activeDate,
        1,
      );
    } else {
      this.calendar.activeDate = this.dateAdapter.addCalendarYears(
        this.calendar.activeDate,
        1,
      );
    }

    this.eventCalendarService.changedActiveMonth.next(this.calendar.activeDate);
  }
}
